/**
 * AgentStatusGrid - Grid premium de cards mostrando status de cada agente
 * 
 * Design com gradiente, ícone decorativo, efeito shimmer e hover effects
 * Paginação integrada com 6 agentes por página (matriz 2x3)
 * 
 * Exibe para cada agente:
 * - Nome e avatar
 * - Status online (🟢🟡🔴⚫)
 * - Conversas ativas
 * - Indicador "Disponível para distribuição"
 * 
 * @version 1.2.0 - Adicionada paginação
 * @date 2025-12-21
 */

import { useState, useEffect } from 'react';
import { Users, MessageSquare, Check, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { SimplePagination } from '@/components/ui/simple-pagination';
import { cn } from '@/lib/utils';
import type { AgentStatus } from '@/hooks/useDistributionMonitor';

// ==============================================================
// Constantes
// ==============================================================

/** Quantidade de agentes por página - matriz 2 colunas x 3 linhas */
const PAGE_SIZE = 6;

// ==============================================================
// Interfaces
// ==============================================================

interface AgentStatusGridProps {
  /** Lista de agentes com status */
  agents: AgentStatus[];
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Helpers
// ==============================================================

/** Retorna cor do indicador de status */
const getStatusColor = (status: AgentStatus['onlineStatus']): string => {
  switch (status) {
    case 'online':
      return 'bg-emerald-500';
    case 'away':
      return 'bg-amber-500';
    case 'busy':
      return 'bg-destructive';
    case 'offline':
    default:
      return 'bg-muted-foreground/50';
  }
};

/** Retorna label traduzido do status */
const getStatusLabel = (status: AgentStatus['onlineStatus']): string => {
  switch (status) {
    case 'online':
      return 'Online';
    case 'away':
      return 'Ausente';
    case 'busy':
      return 'Ocupado';
    case 'offline':
    default:
      return 'Offline';
  }
};

/** Extrai iniciais do nome */
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

// ==============================================================
// Componente Principal
// ==============================================================

export function AgentStatusGrid({ agents, isLoading }: AgentStatusGridProps) {
  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  
  // Cálculos de paginação
  const totalPages = Math.ceil(agents.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedAgents = agents.slice(startIndex, startIndex + PAGE_SIZE);
  
  // Reset para página 1 quando lista de agentes mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [agents.length]);

  // Estado de loading com design premium
  if (isLoading) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-blue-500/5 to-blue-500/10 transition-all duration-300 hover:shadow-lg">
        {/* Efeito shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        {/* Header premium */}
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/10">
            <Users className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Status dos Agentes</h3>
            <p className="text-xs text-muted-foreground">Monitoramento em tempo real</p>
          </div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-20 bg-muted/50 animate-pulse rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Estado vazio
  if (agents.length === 0) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-blue-500/5 to-blue-500/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/10">
            <Users className="h-5 w-5 text-blue-500" />
          </div>
          <h3 className="font-semibold text-foreground">Status dos Agentes</h3>
        </div>
        
        <div className="p-8 text-center">
          <p className="text-sm text-muted-foreground">Nenhum agente encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-blue-500/5 to-blue-500/10 transition-all duration-300 hover:shadow-lg">
      {/* Efeito shimmer no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      {/* Header premium */}
      <div className="relative flex items-center justify-between p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/10 transition-transform duration-300 group-hover:scale-110">
            <Users className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Status dos Agentes</h3>
            <p className="text-xs text-muted-foreground">
              {agents.filter(a => a.onlineStatus === 'online').length} online de {agents.length} total
            </p>
          </div>
        </div>
        
        {/* Badge de contagem */}
        <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
          {agents.length} agentes
        </Badge>
      </div>
      
      {/* Grid de agentes - matriz 2x3 paginada */}
      <div className="relative p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {paginatedAgents.map(agent => (
            <div
              key={agent.userId}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border transition-all duration-200',
                'hover:shadow-md hover:-translate-y-0.5',
                agent.onlineStatus === 'online' 
                  ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10' 
                  : 'border-border/50 bg-card/50 hover:bg-card'
              )}
            >
              {/* Avatar com indicador de status */}
              <div className="relative">
                <Avatar className="h-10 w-10 border-2 border-background shadow-sm">
                  <AvatarImage src={agent.avatarUrl || undefined} />
                  <AvatarFallback className="text-xs bg-muted">
                    {getInitials(agent.fullName)}
                  </AvatarFallback>
                </Avatar>
                <div 
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-background',
                    'ring-2 ring-background',
                    getStatusColor(agent.onlineStatus)
                  )}
                />
              </div>

              {/* Informações do agente */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {agent.fullName}
                  </span>
                  <Badge 
                    variant={agent.onlineStatus === 'online' ? 'default' : 'secondary'}
                    className={cn(
                      'text-[10px] px-1.5 py-0 shrink-0',
                      agent.onlineStatus === 'online' && 'bg-emerald-500 hover:bg-emerald-600'
                    )}
                  >
                    {getStatusLabel(agent.onlineStatus)}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-3 mt-1">
                  {/* Conversas ativas */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MessageSquare className="h-3 w-3" />
                    <span>{agent.activeConversations} conv.</span>
                  </div>

                  {/* Disponível para distribuição */}
                  <div className="flex items-center gap-1 text-xs">
                    {agent.availableForDistribution ? (
                      <>
                        <Check className="h-3 w-3 text-emerald-500" />
                        <span className="text-emerald-600">Disponível</span>
                      </>
                    ) : (
                      <>
                        <X className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">Indisponível</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Paginação - exibe apenas se houver mais de uma página */}
        <SimplePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
